import React, { useState } from 'react';

import Button, { ButtonGroup } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';

import { useFeedbackDispatch } from '../Feedback.reducer';
import { useFeedback } from '../FeedbackProvider';
import {
  FeedbackInputWrapperStyled,
  HeaderWrapperStyled,
  ScreenStyled,
} from './Screens.styled';

const COMMENT_MAX_LENGTH = 1000;
const COMMENT_MIN_LENGTH = 1;
/** One extra character to trigger the validation */
const COMMENT_INPUT_MAX_LENGTH = COMMENT_MAX_LENGTH + 1;

export default function WriteFeedbackScreen(): JSX.Element {
  const dispatch = useFeedbackDispatch();
  const { saveFeedbackComment } = useFeedback();
  const [isCommentInvalid, setIsCommentInvalid] = useState(true);

  const validateComment = (comment: string): 'comment-too-long' | null => {
    if (comment.length > COMMENT_MAX_LENGTH) {
      setIsCommentInvalid(true);
      return 'comment-too-long';
    }

    if (comment.length < COMMENT_MIN_LENGTH) {
      setIsCommentInvalid(true);
      // Don't trigger invalid state on the field
      return null;
    }

    setIsCommentInvalid(false);
    return null;
  };

  const onSkipButtonClick = () => {
    dispatch({
      type: 'click-skip-feedback',
    });
  };

  const onSendFeedbackButtonClick = ({ comment }: { comment: string }) => {
    saveFeedbackComment({ comment: comment.trim() });

    dispatch({
      type: 'click-send-feedback',
    });
  };

  return (
    <ScreenStyled width='large'>
      <HeaderWrapperStyled>
        <h5>Tell us what can we do better.</h5>
        <div>
          Help us improve the app by sharing your feedback and pain points.
        </div>
      </HeaderWrapperStyled>
      <FeedbackInputWrapperStyled>
        <Form onSubmit={onSendFeedbackButtonClick}>
          {({ formProps }) => (
            <form {...formProps}>
              <Field name='comment' validate={validateComment} defaultValue=''>
                {({ fieldProps, error }: any) => (
                  <>
                    <TextArea
                      {...fieldProps}
                      placeholder='Leave us your feedback here (max. 1000 characters)'
                      minimumRows={3}
                      maxHeight='160px'
                      resize='vertical'
                      maxLength={COMMENT_INPUT_MAX_LENGTH}
                      spellCheck
                      autoFocus
                    />
                    {error === 'comment-too-long' && (
                      <ErrorMessage>
                        A comment can be up to 1000 characters long.
                      </ErrorMessage>
                    )}
                  </>
                )}
              </Field>
              <FormFooter>
                <ButtonGroup>
                  <Button appearance='default' onClick={onSkipButtonClick}>
                    Skip
                  </Button>
                  <Button
                    type='submit'
                    appearance='primary'
                    isDisabled={isCommentInvalid}
                  >
                    Send feedback
                  </Button>
                </ButtonGroup>
              </FormFooter>
            </form>
          )}
        </Form>
      </FeedbackInputWrapperStyled>
    </ScreenStyled>
  );
}
